import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useLocation } from 'react-router-dom';
import { Globe } from 'lucide-react';

const languages = [
  { code: 'en', name: 'English', flag: '🇺🇸' },
  { code: 'es', name: 'Español', flag: '🇪🇸' },
  { code: 'fr', name: 'Français', flag: '🇫🇷' },
  { code: 'de', name: 'Deutsch', flag: '🇩🇪' },
  { code: 'pt', name: 'Português', flag: '🇵🇹' },
  { code: 'vi', name: 'Tiếng Việt', flag: '🇻🇳' },
  { code: 'sv', name: 'Svenska', flag: '🇸🇪' },
  { code: 'ro', name: 'Română', flag: '🇷🇴' },
];

const LanguageSelector = () => {
  const { i18n } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const [isOpen, setIsOpen] = useState(false);

  const currentLanguage = languages.find(lang => lang.code === i18n.language) || languages[0];

  const handleLanguageChange = (languageCode: string) => {
    // Update the URL to reflect the new language
    const currentPath = location.pathname;
    const pathSegments = currentPath.split('/').filter(segment => segment);
    
    // Check if the first segment is a language code
    const supportedLanguages = languages.map(lang => lang.code);
    const hasLocaleInPath = pathSegments.length > 0 && supportedLanguages.includes(pathSegments[0]);
    
    let newPath;
    if (hasLocaleInPath) {
      // Replace the existing locale
      pathSegments[0] = languageCode;
      newPath = `/${pathSegments.join('/')}`;
    } else {
      // Add locale to the beginning
      newPath = `/${languageCode}${currentPath}`;
    }
    
    // Ensure the path ends with / for the home page
    if (newPath === `/${languageCode}`) {
      newPath = `/${languageCode}/`;
    }
    
    navigate(newPath);
    setIsOpen(false);
  };

  return (
    <div className="relative">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center space-x-2 text-gray-600 hover:text-gray-900 transition-colors px-2 py-1 rounded-md hover:bg-gray-100 dark:text-gray-400 dark:hover:text-white dark:hover:bg-slate-700"
      >
        <Globe size={16} />
        <span className="text-sm">{currentLanguage.flag}</span>
        <svg
          className={`w-4 h-4 transition-transform ${isOpen ? 'rotate-180' : ''}`}
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>

      {isOpen && (
        <div className="absolute right-0 mt-2 w-48 bg-white border border-gray-200 rounded-lg shadow-xl z-50 dark:bg-slate-800 dark:border-slate-700">
          <div className="py-1">
            {languages.map((language) => (
              <button
                key={language.code}
                onClick={() => handleLanguageChange(language.code)}
                className={`w-full text-left px-4 py-2 text-sm hover:bg-gray-50 flex items-center space-x-3 ${
                  currentLanguage.code === language.code
                    ? 'text-primary bg-gray-50 dark:bg-slate-700'
                    : 'text-gray-700 dark:text-gray-300 dark:hover:bg-slate-700'
                }`}
              >
                <span>{language.flag}</span>
                <span>{language.name}</span>
                {currentLanguage.code === language.code && (
                  <span className="ml-auto text-primary">✓</span>
                )}
              </button>
            ))}
          </div>
        </div>
      )}

      {isOpen && (
        <div 
          className="fixed inset-0 z-40" 
          onClick={() => setIsOpen(false)}
        />
      )}
    </div>
  );
};

export default LanguageSelector;